'use client';

import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import rpcShared from '@rockpapercoin/rpc-shared';
const {
	strings: { errorMessages: ErrorMessages },
} = rpcShared;
import {
	Box,
	FormControl,
	FormHelperText,
	InputLabel,
	Input,
	Link,
	Typography,
} from '@mui/material';
import { Button } from '../../blocks/Button';
import { BrandWordmark } from '../BrandWordmark';
import { Password } from '../Password';
import { useToast } from '../../hooks/useToast';
import styles from './ResetPassword.module.scss';

// logic
import { routeWithDelay } from '../../lib/helpers';
import validations from '../../lib/helpers/validations';
import { toggleLoader } from '../../redux/actions';
import API from '../../lib/API';

export const ResetPassword: React.FC<{
	emailAddress?: string;
	passwordResetToken?: string;
}> = ( { emailAddress, passwordResetToken } ) => {
	const [ email, setEmail ] = useState( emailAddress );
	const [ password, setPassword ] = useState( '' );
	const [ repassword, setRepassword ] = useState( '' );
	const [ validate, setValidate ] = useState<
		{ email?: string; password?: string; repassword?: string } | undefined
	>();

	const dispatch = useDispatch();
	const { showError, showSuccess } = useToast();

	const validateEmailRequest = useCallback( () => {
		const validation = validations.validateEmail( email ) || undefined;

		setValidate( ( prevState ) => ( { ...prevState, email: validation } ) );
		return !validation;
	}, [ email ] );

	const requestPasswordResetEmail = useCallback(
		async ( e: React.FormEvent<HTMLFormElement> ) => {
			e.preventDefault();
			if ( validateEmailRequest() && email ) {
				dispatch( toggleLoader( true ) );
				const response = await API.requestPasswordReset( email );
				if ( 'errors' in response ) {
					response.errors.map( ( error ) => showError( error ) );
				} else if ( response.success ) {
					showSuccess(
						`An email will be sent to ${ email } if there is a user associated with that address.`
					);
				}
				dispatch( toggleLoader( false ) );
			}
		},
		[
			dispatch,
			email,
			showError,
			showSuccess,
			validateEmailRequest
		]
	);

	const validateResetRequest = useCallback( () => {
		if ( passwordResetToken ) {
			const { password: validPass, repassword: validRePass } =
				validations.validatePasswords( password, repassword );
			setValidate( ( prevState ) => ( {
				...prevState,
				password: validPass,
				repassword: validRePass,
			} ) );
			return !validRePass && !validPass;
		}
		return true;
	}, [
		password,
		passwordResetToken,
		repassword
	] );

	const setNewPassword = useCallback(
		async ( e: React.FormEvent<HTMLFormElement> ) => {
			e.preventDefault();
			if ( validateResetRequest() && email && passwordResetToken ) {
				dispatch( toggleLoader( true ) );
				const result = await API.resetPassword( {
					email,
					passwordResetToken,
					password,
					confirmedPassword: repassword,
				} );
				dispatch( toggleLoader( false ) );

				if ( 'errors' in result ) {
					result.errors.map( ( error ) => {
						showError( error, 10000 );
						if (
							error &&
							error.message &&
							error.message.includes( ErrorMessages.invalidLink )
						) {
							return routeWithDelay( '/Login' );
						}
					} );
					return false;
				} else {
					showSuccess( 'Success! You can now log in with your new password.' );
					return routeWithDelay( '/Login' );
				}
			}
		},
		[
			dispatch,
			email,
			password,
			passwordResetToken,
			repassword,
			showError,
			showSuccess,
			validateResetRequest,
		]
	);

	return (
		<Box className={ styles.container } component='main'>
			<Box className={ styles.content }>
				<Box className={ styles.intro }>
					<BrandWordmark className={ styles.brand } />
					<Box className={ styles.copy }>
						<Typography className={ styles.heading }>
							Reset your password.
						</Typography>
						<Typography className={ styles.subtext }>
							Enter your email and we'll send you a link to reset your password.
						</Typography>
					</Box>
				</Box>
				<Box className={ styles.dialogue }>
					{ passwordResetToken ? (
						<>
							<Typography className={ styles.title } data-cy='reset-password'>
								Reset your password
							</Typography>
							<Typography className={ styles.cardSubtext }>
								Enter your new password below.
							</Typography>
							<form
								className={ styles.form }
								onSubmit={ setNewPassword }
								noValidate
							>
								<FormControl variant='standard' className={ styles.input }>
									<InputLabel htmlFor='email' shrink>
										Email
									</InputLabel>
									<Input
										id='email'
										data-cy='email-field'
										slotProps={ { input: { type: 'email', inputMode: 'email' } } }
										value={ email }
										disabled={ true }
									/>
								</FormControl>
								<Password
									className={ styles.input }
									label='Password'
									value={ password }
									variant='standard'
									onChange={ ( e ) => setPassword( e.target.value ) }
									error={ !!validate?.password }
									errorText={ validate?.password }
								/>
								<Password
									className={ styles.input }
									label='Confirm password'
									dataCy='confirmPassword-field'
									value={ repassword }
									variant='standard'
									onChange={ ( e ) => setRepassword( e.target.value ) }
									error={ !!validate?.repassword }
									errorText={ validate?.repassword }
								/>
								<Button
									className={ styles.submitButton }
									data-cy='submit-button'
									type='submit'
									color='secondary'
									variant='contained'
									disabled={ !password || !repassword }
								>
									Change password
								</Button>
							</form>
						</>
					) : (
						<>
							<Typography className={ styles.title } data-cy='forgot-password'>
								Forgot your Password?
							</Typography>
							<Typography className={ styles.cardSubtext }>
								No worries, enter your details below and we will get you back on
								track!
							</Typography>
							<form
								className={ styles.form }
								onSubmit={ requestPasswordResetEmail }
								noValidate
							>
								<FormControl variant='standard' className={ styles.input }>
									<InputLabel htmlFor='email' shrink>
										Email
									</InputLabel>
									<Input
										id='email'
										data-cy='email-field'
										placeholder='you@company.com'
										slotProps={ { input: { type: 'email', inputMode: 'email' } } }
										value={ email }
										onChange={ ( e ) => setEmail( e.target.value ) }
										error={ !!validate?.email }
										onBlur={ () => validateEmailRequest() }
									/>
									{ validate?.email && (
										<FormHelperText className={ styles.error }>
											{ validate.email }
										</FormHelperText>
									) }
								</FormControl>
								<Button
									className={ styles.submitButton }
									data-cy='submit-button'
									type='submit'
									color='secondary'
									disabled={ !email }
								>
									Reset Password
								</Button>
							</form>
						</>
					) }
					<Typography className={ styles.signInLink }>
						Remember your password?{ ' ' }
						<Link href='/Login' data-cy='sign-in-link'>
							Sign in
						</Link>
					</Typography>
				</Box>
			</Box>
		</Box>
	);
};
